import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ReactiveFormsModule, FormGroup, FormControl } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { RouterModule } from '@angular/router';
import { UserInfoInterface } from '@core/models/authentication.model';
import { AuthenticationService } from '@core/services/authentication/authentication.service';
import { PartnerService } from '@feature/dashboard/partners/services/partner.service';
import { PartnerListInterface } from '@feature/dashboard/partners/types/partner-list';
import { Store } from '@ngrx/store';
import { CustomDateRangeHeaderComponent } from '@sharedModule/components/custom-date-range-header/custom-date-range-header.component';
import { InputModule } from '@sharedModule/components/input/input.module';
import { sortDirection } from '@sharedModule/constants/data-grid.constants';
import { ATTRIBUTES_CONSTANT } from '@sharedModule/constants/fields.constant';
import { HasAccessDirective } from '@sharedModule/directives/has-access.directive';
import { HasPlatformAccessDirective } from '@sharedModule/directives/has-platform-access.directive';
import { MaterialModule } from '@sharedModule/material/material.module';
import { ExcelExportService } from '@sharedModule/services/excel-export/excel-export.service';
import { getActiveSort } from '@sharedModule/utils/sort.utils';
import { Subject, Observable, debounceTime, takeUntil } from 'rxjs';
import { TRANSACTION_STATUS } from '../../constant/transaction-list.constant';
import {
  updateTransactionListFilter,
  resetTransactionList,
} from '../../store/transaction-list.actions';
import { selectTransactionList } from '../../store/transaction-list.selector';
import { TransactionListInterface } from '../../types/transaction-list';
import {
  defaultTransactionListFilter,
  TransactionListFilter,
} from '../../types/transaction-list-filter';

@Component({
  selector: 'app-transaction-list',
  standalone: true,
  imports: [
    InputModule,
    CommonModule,
    RouterModule,
    MaterialModule,
    HasAccessDirective,
    ReactiveFormsModule,
    HasPlatformAccessDirective,
  ],
  templateUrl: './transaction-list.component.html',
  styleUrl: './transaction-list.component.scss',
})
export class TransactionListComponent implements OnInit, OnDestroy {
  public readonly attrConstant = ATTRIBUTES_CONSTANT.transactions;
  public readonly CustomDateRangeHeader = CustomDateRangeHeaderComponent;
  filterForm!: FormGroup;
  transactionStatus = TRANSACTION_STATUS;
  displayedColumns: string[] = [
    'trans_session_id',
    'created_at',
    'created_by',
    'transaction_status',
    'source_amount',
    'destination_amount',
    'sender_first_name',
    'beneficiary_first_name',
  ];
  transactionList?: TransactionListInterface[];
  dataSource!: MatTableDataSource<TransactionListInterface>;
  activeSort?: Sort;
  fallBackSort = {
    active: 'created_at',
    direction: sortDirection.desc,
  };
  page = 0;
  total = 0;
  limit = 10;
  subject = new Subject<{
    partner_id: number;
    search: string;
    current_status: string;
    from_date: string;
    to_date: string;
  }>();
  userInfo?: UserInfoInterface;
  partnerList!: Observable<PartnerListInterface[]>;
  isSuperAdmin!: boolean;
  #destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private partnerService: PartnerService,
    private excelExportService: ExcelExportService,
    private authenticationService: AuthenticationService
  ) {
    this.userInfo = this.authenticationService.getUserInfo();
    this.isSuperAdmin = this.authenticationService.isSuperAdmin();
  }

  ngOnInit(): void {
    this.filterFormInit();
    this.loadData();
    this.dispatchFilter();
    const debounceTimeDuration = 1000;
    this.subject
      .pipe(debounceTime(debounceTimeDuration), takeUntil(this.#destroy$))
      .subscribe(res => {
        const partnerId = this.filterForm.value[this.attrConstant.partner.key];
        const status = this.filterForm.value[this.attrConstant.status.key];
        this.dispatchFilter({
          count: 10,
          page: 0,
          ...res,
          partner_id: ['', null].includes(partnerId) ? null : [partnerId],
          current_status: ['', null].includes(status) ? null : [status],
        });
      });
    if (this.authenticationService.isSuperAdmin())
      this.partnerList = this.partnerService.sharedData$;
  }

  filterFormInit(): void {
    this.filterForm = new FormGroup({
      [this.attrConstant.partner.key]: new FormControl(''),
      [this.attrConstant.search.key]: new FormControl(''),
      [this.attrConstant.status.key]: new FormControl(''),
      [this.attrConstant.from_date.key]: new FormControl(''),
      [this.attrConstant.to_date.key]: new FormControl(''),
    });
  }

  loadData(): void {
    this.store
      .select(selectTransactionList)
      .pipe(takeUntil(this.#destroy$))
      .subscribe(data => {
        if (data) {
          this.transactionList = data.rawData;
          this.dataSource = new MatTableDataSource(data.rawData);
          this.page = data.page;
          this.total = data.totalRecords;
          this.limit = data.count;
        }
      });
  }

  applyFilter(): void {
    this.subject.next(this.filterForm.value);
  }

  clearFilters(): void {
    this.filterForm.reset();
    this.dispatchFilter({ ...defaultTransactionListFilter });
  }

  reload(): void {
    this.dispatchFilter();
  }

  onPageChange(pageEvent: PageEvent): void {
    this.dispatchFilter({
      count: pageEvent.pageSize,
      page: pageEvent.pageIndex,
    });
  }

  onSortChange(sortEvent: Sort): void {
    this.activeSort = getActiveSort(sortEvent, this.fallBackSort);
    this.dispatchFilter({
      sort_key: this.activeSort.active,
      sort_by: this.activeSort.direction,
    });
  }

  dispatchFilter(transactionListFilter?: TransactionListFilter): void {
    this.store.dispatch(
      updateTransactionListFilter({ filter: transactionListFilter ?? {} })
    );
  }

  export(): void {
    this.excelExportService.exportToExcel(
      this.transactionList!,
      'transactions'
    );
  }

  ngOnDestroy(): void {
    this.store.dispatch(resetTransactionList());
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
